// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { list, downloadData } from "aws-amplify/storage";
import ButtonSelectionList from "./ButtonSelectionList";
import DataTypeFormAssetForecast from "./DataTypeFormAssetForecast";
import DataSourceStyleUpdate from "./DataSourceStyleUpdate/DataSourceStyleUpdate";
import GEFSHRRRModelDataSourceForm from "./GEFSHRRRModelDataSourceForm";
import { fetchDataSources } from "../../api/awsLamba";
import { extractDateFromFilename } from "./utils";

import Select from "../ReusableComponents/Select";
// Define data source types
const DATA_SOURCE_TYPES = [
  { label: "Asset Forecast", value: "assetForecasts", disabled: false },
  { label: "Additional Data Sources", value: "additionalSources", disabled: true },
  { label: "Upload Data", value: "uploadData", disabled: true }
];

// Selection step enum
export const SELECTION_STEPS = {
  SOURCE_TYPE: 'SOURCE_TYPE',
  ASSET: 'ASSET',
  FORECAST_TYPE: 'FORECAST_TYPE',
  MODEL: 'MODEL',
  STYLE: 'STYLE'
};

const ButtonSelectionDataSourceForm = ({
  selectedAsset,
  onSubmitForm,
  dataSourceRecord,
  onDataSourceChange,
  onCancel,
  initialStep = SELECTION_STEPS.SOURCE_TYPE
}) => {
  console.log("ButtonSelectionDataSourceForm constructor - initialStep:", initialStep);
  
  // States for the selection process Force the step to match the exact string value to avoid reference issues
  const [currentStep, setCurrentStep] = useState(
    initialStep === "STYLE" ? "STYLE" : 
    initialStep === SELECTION_STEPS.STYLE ? SELECTION_STEPS.STYLE : 
    initialStep || SELECTION_STEPS.SOURCE_TYPE
  );
  const [selectedSourceType, setSelectedSourceType] = useState(null);
  const [selectedAssetObj, setSelectedAssetObj] = useState(null);
  const [selectedForecastType, setSelectedForecastType] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [autoSubmitMode, setAutoSubmitMode] = useState(false);
  
  // Log the current step for debugging
  useEffect(() => {
    console.log("Current selection step is now:", currentStep);
  }, [currentStep]);
  
  // States for model dates
  const [modelDates, setModelDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  
  // Effect to initialize the form state if we're starting from the Style step
  useEffect(() => {
    console.log("ButtonSelectionDataSourceForm initialStep:", initialStep);
    
    if (initialStep === SELECTION_STEPS.STYLE || initialStep === "STYLE") {
      // Force the current step to STYLE
      setCurrentStep(SELECTION_STEPS.STYLE);
      // If we're editing an existing data source, we need to load its configuration
      if (dataSourceRecord) {
        const [dataSourceId] = Object.keys(dataSourceRecord);
        const record = dataSourceRecord[dataSourceId];
        
        // Set source type
        setSelectedSourceType(DATA_SOURCE_TYPES[0]); // Asset Forecast
        
        // Set asset info
        if (record.config?.assetId && record.config?.assetName) {
          setSelectedAssetObj({
            id: record.config.assetId,
            name: record.config.assetName
          });
        }
        
        // Set forecast type
        if (record.config?.modelType) {
          const forecastType = record.config.modelType.includes('sensitivity') 
            ? { label: "Sensitivity Models", value: "sensitivityModels" }
            : { label: "Processed Models", value: "processedModels" };
          setSelectedForecastType(forecastType);
        }
        
        // For the model, we can't fully reconstruct it without fetching, but we can set a placeholder that will allow the style step to work
        const modelPlaceholder = {
          id: record.resource?.recordId,
          model: {
            id: record.resource?.recordId,
            routeShort: record.resource?.routeShort,
            routeLong: record.resource?.routeLong
          }
        };
        setSelectedModel(modelPlaceholder);
        
        // For edited data sources, we need to fetch the actual dates for the model
        if (modelPlaceholder.model.routeShort) {
          // We'll immediately trigger the proper date fetch for this model
          fetchModelDates({model: modelPlaceholder.model});
          
          // But we also set a temporary date for now
          if (record.resource?.fileSelected) {
            const currentDate = {
              eTag: "current", // This is used as the select value
              path: record.resource.fileSelected,
              dateStr: record.resource.dateIssued || "unknown",
              displayDate: record.resource.dateIssued ? 
                record.resource.dateIssued.replace(/-/g, '/') : 
                "Current version"
            };
            
            setSelectedDate(currentDate);
          }
        }
      }
    }
  }, [initialStep, dataSourceRecord]);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Get assets from Redux store
  const { assets, assetGroups } = useSelector(({ assets, assetGroups }) => {
    const FlattenedAssets = Object.values(assets.dataGroups)
      .flatMap(assetGroup => assetGroup);
    
    return {
      assets: FlattenedAssets,
      assetGroups: assetGroups.groups
    };
  });

  // Format assets for display in button list
  const formattedAssets = assets.map(asset => ({
    label: `${asset.name} - ${assetGroups[asset.assetGroupId]?.name || 'Unknown Group'}`,
    value: asset.id,
    asset: asset,
    // Add group names to each asset for display in the two-line format
    groupNames: assetGroups,
    disabled: false
  }));
  
  // Log when selectedAssetObj changes
  useEffect(() => {
    if (selectedAssetObj) {
      console.log("Selected asset in ButtonSelectionDataSourceForm:", {
        name: selectedAssetObj.name,
        id: selectedAssetObj.id,
        hasProcessedModels: selectedAssetObj?.processedModels?.items?.length > 0,
        processedModelsCount: selectedAssetObj?.processedModels?.items?.length || 0
      });
    }
  }, [selectedAssetObj]);

  // Handle data source type selection
  const handleSourceTypeSelect = (sourceType) => {
    setSelectedSourceType(sourceType);
    setCurrentStep(SELECTION_STEPS.ASSET);
  };

  // Handle asset selection
  const handleAssetSelect = (assetItem) => {
    const asset = assetItem.asset;
    setSelectedAssetObj(asset);
    
    // Determine available forecast types for this asset
    let forecastTypes = [];
    
    // Debug processed models in more detail
    console.log("🔍 ASSET SELECTION DEBUG 🔍");
    console.log("Selected asset:", {
      id: asset?.id,
      name: asset?.name,
      description: asset?.description
    });
    
    console.log("Has processedModels property:", !!asset?.processedModels);
    console.log("processedModels.items exists:", !!asset?.processedModels?.items);
    console.log("processedModels.items is array:", Array.isArray(asset?.processedModels?.items));
    
    if (asset?.processedModels?.items) {
      const modelCount = asset.processedModels.items.length;
      console.log(`📊 Found ${modelCount} models for asset ${asset.name}`);
      
      // Identify model types
      const processedModels = asset.processedModels.items.filter(m => m.modelType === "PROCESSED");
      const sensitivityModels = asset.processedModels.items.filter(m => m.modelType === "SENSITIVITY");
      const unknownModels = asset.processedModels.items.filter(m => !m.modelType);
      
      console.log(`  - Processed models: ${processedModels.length}`);
      console.log(`  - Sensitivity models: ${sensitivityModels.length}`);
      console.log(`  - Unknown type models: ${unknownModels.length}`);
      
      // Detailed inspection of each model
      console.log("📋 DETAILED MODEL INSPECTION:");
      asset.processedModels.items.forEach((model, idx) => {
        console.log(`Model ${idx+1} of ${modelCount}:`);
        console.log(`  - ID: ${model.id || 'missing'}`);
        console.log(`  - Type: ${model.modelType || 'missing'}`);
        console.log(`  - Label: ${model.label || 'missing'}`);
        console.log(`  - Properties Label: ${model.properties?.label || 'missing'}`);
        console.log(`  - Name: ${model.name || 'missing'}`);
        console.log(`  - ModelName: ${model.modelName || 'missing'}`);
        console.log(`  - RouteShort: ${model.routeShort || 'missing'} ⚠️`);
        console.log(`  - RouteLong: ${model.routeLong || 'missing'} ⚠️`);
        
        // Check if model has the minimum viable properties
        const hasMinViableProps = model.id && model.modelType;
        const hasRouteProps = model.routeShort && model.routeLong;
        console.log(`  - Has minimum viable properties: ${hasMinViableProps ? '✅' : '❌'}`);
        console.log(`  - Has route properties: ${hasRouteProps ? '✅' : '❌'}`);
        
        // Log all available properties for further debugging
        console.log(`  - All available properties: ${Object.keys(model).join(', ')}`);
        
        // Dump full model for debugging
        console.log("  Full model object:", model);
      });
    } else {
      console.warn("❌ No processedModels.items found for asset:", asset.name);
    }
    
    if(asset?.processedModels?.items?.some(item => item.modelType === "PROCESSED")) {
      forecastTypes.push({ 
        label: "Processed Models", 
        value: "processedModels", 
        disabled: false 
      });
    }
    
    if(asset?.processedModels?.items?.some(item => item.modelType === "SENSITIVITY")) {
      forecastTypes.push({ 
        label: "Sensitivity Models", 
        value: "sensitivityModels", 
        disabled: false 
      });
    }
    
    // Add GEFS and HRRR forecast types - enable these options to use the caching process
    forecastTypes = forecastTypes.concat([
      { label: "10 Day Forecast (GEFS)", value: "240hr3", disabled: false },
      { label: "2 Day Forecast (HRRR)", value: "48hr1", disabled: false }
    ]);
    
    console.log("🚧 AUTO-SELECTION CHECK - Available forecast types:", forecastTypes);
    
    // IMPORTANT: Don't auto-select anymore - this causes issues with model loading Instead, always show the forecast type selection step
    setForecastTypes(forecastTypes);
    setCurrentStep(SELECTION_STEPS.FORECAST_TYPE);
    
    // Log for debugging
    console.log("🚦 Moving to FORECAST_TYPE step, disabled auto-selection");
    
    /* DISABLED AUTO-SELECTION LOGIC: // If there's only one enabled forecast type, select it automatically const enabledTypes = forecastTypes.filter(type => !type.disabled); if (enabledTypes.length === 1) { handleForecastTypeSelect(enabledTypes[0]); } else { // Otherwise go to forecast type selection setForecastTypes(forecastTypes); setCurrentStep(SELECTION_STEPS.FORECAST_TYPE); } */
  };

  // State for forecast types (populated after asset selection)
  const [forecastTypes, setForecastTypes] = useState([]);

  // Handle forecast type selection
  const handleForecastTypeSelect = (forecastType) => {
    setSelectedForecastType(forecastType);
    
    console.log("🔍 FORECAST TYPE SELECTION DEBUG 🔍");
    console.log("Selected forecast type:", forecastType);
    console.log("Selected asset:", selectedAssetObj?.name);
    
    // Process models based on forecast type
    if (forecastType.value === 'processedModels') {
      console.log("👉 Loading PROCESSED models for asset:", selectedAssetObj?.name);
      
      console.log("Original models in asset:", selectedAssetObj?.processedModels?.items?.length || 0);
      
      // Log all model types before filtering
      if (selectedAssetObj?.processedModels?.items) {
        const modelTypes = selectedAssetObj.processedModels.items.map(item => item.modelType);
        console.log("All model types before filtering:", modelTypes);
      }
      
      // Filter to get only PROCESSED models
      const processedModels = selectedAssetObj?.processedModels?.items?.filter(
        item => item.modelType === "PROCESSED"
      ) || [];
      
      console.log(`🔎 Found ${processedModels.length} PROCESSED models after filtering`);
      processedModels.forEach((model, idx) => {
        console.log(`Model ${idx+1} ID: ${model.id}, Type: ${model.modelType}`);
      });
      
      // Check for critical issues that might prevent models from showing up
      const missingProperties = processedModels.filter(model => !model.routeShort || !model.routeLong);
      if (missingProperties.length > 0) {
        console.warn(`⚠️ WARNING: ${missingProperties.length} models are missing routeShort or routeLong:`, 
                    missingProperties.map(model => ({
                      id: model.id,
                      hasRouteShort: !!model.routeShort,
                      hasRouteLong: !!model.routeLong
                    })));
      }
      
      if (processedModels.length === 0) {
        console.error("❌ NO PROCESSED MODELS FOUND after filtering!");
        // Debug selected asset again to verify the models were there in the first place
        console.log("Re-checking asset:", {
          name: selectedAssetObj?.name,
          processedModelsItems: selectedAssetObj?.processedModels?.items,
          count: selectedAssetObj?.processedModels?.items?.length || 0
        });
      }
      
      // Print the entire processed models array for debugging
      console.log(`Found ${processedModels.length} processed models for selected asset`);
      console.log('Processed Models array:', JSON.stringify(processedModels, null, 2));
      
      console.log("=================== MODEL DEBUG START ===================");
      // IMPORTANT: Print everything we can about these models
      console.log("PROCESSED MODELS RAW:", processedModels);
      console.log("PROCESSED MODELS KEYS:", processedModels.map(model => Object.keys(model)));
      
      // Deep inspection of each model's structure
      processedModels.forEach((model, index) => {
        console.log(`\n------ MODEL ${index} DETAILED INSPECTION ------`);
        
        // Specifically check for label property at any depth
        const findLabelProperty = (obj, path = '') => {
          if (!obj || typeof obj !== 'object') return;
          
          // Check each property of the object
          Object.entries(obj).forEach(([key, value]) => {
            const currentPath = path ? `${path}.${key}` : key;
            
            // Log if this property is named 'label'
            if (key === 'label') {
              console.log(`🔍 FOUND LABEL at ${currentPath}:`, value);
            }
            
            // Recursively check nested objects
            if (value && typeof value === 'object' && !Array.isArray(value)) {
              findLabelProperty(value, currentPath);
            }
          });
        };
        
        // Run the deep label search
        console.log("⚡ SEARCHING FOR LABEL PROPERTIES IN MODEL:");
        findLabelProperty(model);
        
        // Also do the standard property logging
        Object.keys(model).forEach(key => {
          console.log(`Property ${key}:`, model[key]);
          
          // Check if this property is an object that might contain a name
          if (model[key] && typeof model[key] === 'object') {
            console.log(`Sub-properties of ${key}:`, Object.keys(model[key]));
            
            // Special focus on properties.label which is what we're looking for
            if (key === 'properties' && model[key].label) {
              console.log('FOUND PROCESSED MODEL properties.label:', model[key].label);
            }
          }
        });
        
        // Try to extract specific nested properties
        console.log("Model ID:", model.id);
        console.log("Model name:", model.name);
        console.log("Model createdAt:", model.createdAt);
        console.log("Model updatedAt:", model.updatedAt);
        console.log("Model modelName:", model.modelName);
        console.log("Model modelType:", model.modelType);
        
        if (model.properties) console.log("Model properties:", model.properties);
        if (model.asset) console.log("Model asset:", model.asset);
        if (model.processedModel) console.log("Model processedModel:", model.processedModel);
      });
      console.log("=================== MODEL DEBUG END ===================");
      
      // Get the actual names from the asset model if possible This is to work around possible API issues
      console.log("Looking at selected asset's processed models for name mapping:");
      const modelIdToNameMap = {};
      
      if (selectedAssetObj && selectedAssetObj.processedModels && selectedAssetObj.processedModels.items) {
        selectedAssetObj.processedModels.items.forEach(item => {
          // Create a mapping from ID to name for all processed models in the asset
          if (item.id) {
            // Store name using the best available property
            const name = item.name || 
                       (item.attributes && item.attributes.name) || 
                       item.modelName ||
                       (item.modelType === "PROCESSED" ? "Processed Model" : item.modelType);
            
            modelIdToNameMap[item.id] = name;
            console.log(`Model ID mapping: ${item.id} -> ${name}`);
          }
        });
      }
      
      console.log("▶️ FORMATTING MODELS FOR UI");
      
      // Format the models with names from our mapping if available
      const formattedModels = processedModels.map((model, idx) => {
        console.log(`📝 Formatting model ${idx+1} of ${processedModels.length}`);
        
        // Check if this model has all required properties to be usable
        const isModelUsable = model.id && (
          (model.routeShort && model.routeLong) || // Either has both route properties
          (model.modelName || model.name || model.label) // Or has enough identifying information
        );
        
        console.log(`  - Model ID: ${model.id}`);
        console.log(`  - Model Type: ${model.modelType}`);
        
        // If model is missing critical properties, flag it
        if (!isModelUsable) {
          console.warn("⚠️ Model is missing critical properties:", {
            id: model.id,
            modelType: model.modelType,
            hasRouteShort: !!model.routeShort,
            hasRouteLong: !!model.routeLong,
            hasModelName: !!model.modelName,
            hasLabel: !!model.label,
            hasName: !!model.name
          });
        } else {
          console.log(`  - Model is usable: ✅`);
        }
        
        let displayName;
        
        // First check our ID to name mapping from the asset
        if (model.id && modelIdToNameMap[model.id]) {
          displayName = modelIdToNameMap[model.id];
          console.log(`Using name from asset mapping: ${displayName} for ID ${model.id}`);
        }
        // Special case for well-known models
        else if (model.id && model.id.includes('GEFS')) {
          displayName = "NOAA Weather Service - GEFS Model";
        } else if (model.id && model.id.includes('GFS')) {
          displayName = "NOAA Weather Service - GFS Model";
        } else {
          // If we can find a natural language name anywhere, use it
          displayName = 
            // First try the direct label property - this appears to be most accurate based on debug logs
            model.label ||
            // Then try properties.label 
            (model.properties && model.properties.label) ||
            // Then other property fields
            (model.properties && model.properties.name) ||
            (model.properties && model.properties.display_name) ||
            // Then try other common name fields
            model.displayName ||
            model.display_name ||
            model.model_name ||
            model.modelName ||
            model.model_display_name ||
            // Try attributes
            (model.attributes && model.attributes.name) ||
            // Format routeShort if available
            (model.routeShort ? model.routeShort.split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ') : null) ||
            // Last resorts - only show a short ID without the model type since we show that separately now
            `Processed Model ${model.id?.substring(0, 8)}...`;
        }
        
        console.log(`Final display name for processed model ${model.id}:`, {
          displayName,
          hasPropertiesLabel: model.properties && model.properties.label ? true : false,
          propertiesLabel: model.properties && model.properties.label ? model.properties.label : 'N/A',
          routeShort: model.routeShort || 'N/A'
        });
        
        // Create a clean model object with all required properties
        const formattedModel = {
          label: displayName,
          value: model.id,
          model: { 
            ...model,
            // Ensure modelType is set (helps with UI)
            modelType: model.modelType || "PROCESSED",
            // Ensure ID is present
            id: model.id || `unknown-${Math.random().toString(36).substring(2, 10)}`
          },
          // Only disable if really missing an ID
          disabled: !model.id
        };
        
        console.log(`Formatted model object for UI:`, {
          label: formattedModel.label,
          value: formattedModel.value,
          modelId: formattedModel.model.id,
          disabled: formattedModel.disabled
        });
        
        return formattedModel;
      });
      
      // Filter out models that don't have ID
      const validModels = formattedModels.filter(model => model.model && model.model.id);
      
      console.log(`📊 MODELS SUMMARY: ${formattedModels.length} models formatted, ${validModels.length} valid models with IDs`);
      
      // Log validation failures
      const invalidModels = formattedModels.filter(model => !model.model || !model.model.id);
      if (invalidModels.length > 0) {
        console.warn(`⚠️ ${invalidModels.length} models were invalid:`, invalidModels);
      }
      
      if (validModels.length === 0) {
        // No valid models, show an error or warning
        console.error("❌ NO VALID MODELS FOUND - Creating dummy model as fallback");
        
        // Create a dummy model to show the user something
        const dummyModel = {
          label: "Processed Model (Missing Data)",
          value: "dummy",
          model: {
            id: "dummy",
            modelType: "PROCESSED",
            label: "Processed Model (Missing Data)"
          },
          disabled: true
        };
        
        console.log("Setting models state with dummy model:", dummyModel);
        setModels([dummyModel]);
        setCurrentStep(SELECTION_STEPS.MODEL);
      } else {
        // Always let user choose, even if there's just one model This prevents issues with auto-selection
        console.log(`✅ ${validModels.length} valid models found - letting user choose`);
        console.log("Setting models state with:", validModels.map(m => m.label));
        setModels(validModels);
        setCurrentStep(SELECTION_STEPS.MODEL);
        
        /* DISABLED AUTO-SELECTION LOGIC: if (validModels.length === 1) { // If there's only one model, select it automatically console.log("✅ Single valid model found - auto-selecting:", validModels[0].label); handleModelSelect(validModels[0]); } else { // Multiple models, let user choose console.log(`✅ ${validModels.length} valid models found - letting user choose`); console.log("Setting models state with:", validModels.map(m => m.label)); setModels(validModels); setCurrentStep(SELECTION_STEPS.MODEL); } */
      }
    } 
    else if (forecastType.value === 'sensitivityModels') {
      console.log("Loading SENSITIVITY models for asset:", selectedAssetObj?.name);
      
      const sensitivityModels = selectedAssetObj?.processedModels?.items?.filter(
        item => item.modelType === "SENSITIVITY"
      ) || [];
      
      // Print the entire sensitivity models array for debugging
      console.log(`Found ${sensitivityModels.length} sensitivity models for selected asset`);
      console.log('Sensitivity Models array:', JSON.stringify(sensitivityModels, null, 2));
      
      console.log("=================== SENSITIVITY MODEL DEBUG START ===================");
      // IMPORTANT: Print everything we can about these models
      console.log("SENSITIVITY MODELS RAW:", sensitivityModels);
      console.log("SENSITIVITY MODELS KEYS:", sensitivityModels.map(model => Object.keys(model)));
      
      // Deep inspection of each model's structure
      sensitivityModels.forEach((model, index) => {
        console.log(`\n------ SENSITIVITY MODEL ${index} DETAILED INSPECTION ------`);
        
        // Specifically check for label property at any depth
        const findLabelProperty = (obj, path = '') => {
          if (!obj || typeof obj !== 'object') return;
          
          // Check each property of the object
          Object.entries(obj).forEach(([key, value]) => {
            const currentPath = path ? `${path}.${key}` : key;
            
            // Log if this property is named 'label'
            if (key === 'label') {
              console.log(`🔍 FOUND LABEL at ${currentPath}:`, value);
            }
            
            // Recursively check nested objects
            if (value && typeof value === 'object' && !Array.isArray(value)) {
              findLabelProperty(value, currentPath);
            }
          });
        };
        
        // Run the deep label search
        console.log("⚡ SEARCHING FOR LABEL PROPERTIES IN SENSITIVITY MODEL:");
        findLabelProperty(model);
        
        // Also do the standard property logging
        Object.keys(model).forEach(key => {
          console.log(`Property ${key}:`, model[key]);
          
          // Check if this property is an object that might contain a name
          if (model[key] && typeof model[key] === 'object') {
            console.log(`Sub-properties of ${key}:`, Object.keys(model[key]));
            
            // Special focus on properties.label which is what we're looking for
            if (key === 'properties' && model[key].label) {
              console.log('FOUND SENSITIVITY MODEL properties.label:', model[key].label);
            }
          }
        });
        
        // Try to extract specific nested properties
        console.log("Model ID:", model.id);
        console.log("Model name:", model.name);
        console.log("Model createdAt:", model.createdAt);
        console.log("Model updatedAt:", model.updatedAt);
        console.log("Model modelName:", model.modelName);
        console.log("Model modelType:", model.modelType);
        
        if (model.properties) console.log("Model properties:", model.properties);
        if (model.asset) console.log("Model asset:", model.asset);
        if (model.processedModel) console.log("Model processedModel:", model.processedModel);
      });
      console.log("=================== SENSITIVITY MODEL DEBUG END ===================");
      
      // Get the actual names from the asset model if possible - same approach for sensitivity models
      console.log("Looking at selected asset's sensitivity models for name mapping:");
      const sensitivityModelIdToNameMap = {};
      
      if (selectedAssetObj && selectedAssetObj.processedModels && selectedAssetObj.processedModels.items) {
        // Filter for sensitivity models
        const sensitivityItems = selectedAssetObj.processedModels.items.filter(
          item => item.modelType === "SENSITIVITY"
        );
        
        sensitivityItems.forEach(item => {
          // Create a mapping from ID to name for all sensitivity models in the asset
          if (item.id) {
            // Store name using the best available property
            const name = item.name || 
                      (item.attributes && item.attributes.name) || 
                      item.modelName ||
                      "Sensitivity Analysis Model";
            
            sensitivityModelIdToNameMap[item.id] = name;
            console.log(`Sensitivity Model ID mapping: ${item.id} -> ${name}`);
          }
        });
      }
      
      // Format the models with names from our mapping if available
      const formattedModels = sensitivityModels.map(model => {
        let displayName;
        
        // First check our ID to name mapping from the asset
        if (model.id && sensitivityModelIdToNameMap[model.id]) {
          displayName = sensitivityModelIdToNameMap[model.id];
          console.log(`Using name from asset mapping: ${displayName} for ID ${model.id}`);
        }
        // Special case for well-known models
        else if (model.id && model.id.includes('GEFS')) {
          displayName = "NOAA Weather Service - GEFS Sensitivity Model";
        } else if (model.id && model.id.includes('GFS')) {
          displayName = "NOAA Weather Service - GFS Sensitivity Model";
        } else {
          // If we can find a natural language name anywhere, use it
          displayName = 
            // First try the direct label property - this appears to be most accurate based on debug logs
            model.label ||
            // Then try properties.label 
            (model.properties && model.properties.label) ||
            // Then other property fields
            (model.properties && model.properties.name) ||
            (model.properties && model.properties.display_name) ||
            // Then try other common name fields
            model.displayName ||
            model.display_name ||
            model.model_name ||
            model.modelName ||
            model.model_display_name ||
            // Try attributes
            (model.attributes && model.attributes.name) ||
            // Format routeShort if available
            (model.routeShort ? model.routeShort.split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ') : null) ||
            // Last resorts - only show a short ID without the model type since we show that separately now
            `Model ${model.id?.substring(0, 8)}...`;
        }
        
        console.log(`Final display name for sensitivity model ${model.id}:`, {
          displayName,
          hasPropertiesLabel: model.properties && model.properties.label ? true : false,
          propertiesLabel: model.properties && model.properties.label ? model.properties.label : 'N/A',
          routeShort: model.routeShort || 'N/A'
        });
          
        // Create a clean model object with all required properties
        const formattedModel = {
          label: displayName,
          value: model.id,
          model: { 
            ...model,
            // Ensure modelType is set (helps with UI)
            modelType: model.modelType || "SENSITIVITY",
            // Ensure ID is present
            id: model.id || `unknown-${Math.random().toString(36).substring(2, 10)}`
          },
          // Only disable if really missing an ID
          disabled: !model.id
        };
        
        console.log(`Formatted sensitivity model object for UI:`, {
          label: formattedModel.label,
          value: formattedModel.value,
          modelId: formattedModel.model.id,
          disabled: formattedModel.disabled
        });
        
        return formattedModel;
      });
      
      // Filter out models that don't have ID
      const validModels = formattedModels.filter(model => model.model && model.model.id);
      
      console.log(`Found ${formattedModels.length} sensitivity models, ${validModels.length} valid models with IDs`);
      
      if (validModels.length === 0) {
        // No valid models, show an error or warning
        console.error("No valid sensitivity models found for this asset");
        // Create a dummy model to show the user something
        const dummyModel = {
          label: "Sensitivity Model (Missing Data)",
          value: "dummy",
          model: {
            id: "dummy",
            modelType: "SENSITIVITY",
            label: "Sensitivity Model (Missing Data)"
          },
          disabled: true
        };
        setModels([dummyModel]);
        setCurrentStep(SELECTION_STEPS.MODEL);
      } else {
        // Always let user choose models - no auto-selection This prevents issues with model loading
        console.log(`✅ ${validModels.length} valid sensitivity models found - letting user choose`);
        console.log("Setting models state with:", validModels.map(m => m.label));
        setModels(validModels);
        setCurrentStep(SELECTION_STEPS.MODEL);
        
        /* DISABLED AUTO-SELECTION LOGIC: if (validModels.length === 1) { // If there's only one model, select it automatically handleModelSelect(validModels[0]); } else { // Multiple models, let user choose setModels(validModels); setCurrentStep(SELECTION_STEPS.MODEL); } */
      }
    }
    else if (forecastType.value === '240hr3' || forecastType.value === '48hr1') {
      // For GEFS (10 Day) and HRRR (2 Day) forecast types, we now follow the two-step approach
      console.log(`Loading cached forecast model selection for ${forecastType.label}`);
      
      // Move to model selection step first
      setCurrentStep(SELECTION_STEPS.MODEL);
    }
    else {
      // For other unsupported forecast types
      console.log("Unsupported forecast type:", forecastType.value);
      setCurrentStep(SELECTION_STEPS.STYLE);
    }
  };

  // State for models (populated after forecast type selection)
  const [models, setModels] = useState([]);

  // Function to fetch available dates for a model
  const fetchModelDates = async (model) => {
    if (!model || !model.model) {
      console.error("Cannot fetch dates - model is missing");
      return;
    }
    
    if (!model.model.routeLong) {
      console.error("Cannot fetch dates - routeLong is missing from model:", model.model.id);
      // Set a dummy date since we can't fetch real dates
      const dummyDate = {
        path: "dummy",
        eTag: "dummy",
        file: "latest",
        dateStr: new Date().toISOString(),
        displayDate: "Latest Version (No Date Information)"
      };
      setModelDates([dummyDate]);
      
      // Don't automatically set selectedDate if we're in edit mode and already have a date
      if (!selectedDate) {
        setSelectedDate(dummyDate);
      }
      return;
    }
    
    setIsLoadingDates(true);
    
    try {
      console.log("Fetching model dates from path:", model.model.routeLong);
      
      const result = await list({
        path: model.model.routeLong,
      });
      
      // Process the list items to extract dates
      const dateItems = result.items
        .map(item => {
          const file = item.path.replace(model.model.routeLong, "");
          const dateStr = extractDateFromFilename(file);
          
          return {
            path: item.path,
            eTag: item.eTag,
            file: file,
            dateStr: dateStr || file,
            // Generate a display date that's more user-friendly
            displayDate: dateStr 
              ? (() => {
                  // Log the date string for debugging
                  console.log("Date string to parse:", dateStr);
                  
                  // Parse the date string manually to avoid "Invalid Date" issues Format is typically YYYY-MM-DD-HH
                  try {
                    const parts = dateStr.split('-');
                    if (parts.length >= 4) {
                      const year = parseInt(parts[0]);
                      const month = parseInt(parts[1]) - 1; // JS months are 0-based
                      const day = parseInt(parts[2]);
                      const hour = parseInt(parts[3]);
                      
                      const date = new Date(year, month, day, hour);
                      
                      // Check if date is valid
                      if (isNaN(date.getTime())) {
                        console.log("Invalid date from parts:", {year, month, day, hour});
                        return `Date ${dateStr}`;
                      }
                      
                      return date.toLocaleString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric'
                      });
                    }
                  } catch (e) {
                    console.error("Error parsing date:", e);
                  }
                  
                  // Fallback to a formatted version of the raw string
                  return `Date ${dateStr.replace(/-/g, '/')}`;
                })()
              : file
          };
        })
        // Sort by date (most recent first)
        .sort((a, b) => (b.dateStr || '').localeCompare(a.dateStr || ''));
      
      console.log("Found model dates:", dateItems);
      setModelDates(dateItems);
      
      // Automatically select the most recent date if available and no date is currently selected
      if (dateItems.length > 0 && !selectedDate) {
        setSelectedDate(dateItems[0]);
      }
      
      // If we're in edit mode and have a file path from the current data source, try to find and select the matching date from the fetched dates
      if (initialStep === SELECTION_STEPS.STYLE && dataSourceRecord) {
        const [dataSourceId] = Object.keys(dataSourceRecord);
        const record = dataSourceRecord[dataSourceId];
        if (record.resource?.fileSelected) {
          // Try to find the matching date item
          const matchingDateItem = dateItems.find(item => 
            item.path === record.resource.fileSelected || 
            item.dateStr === record.resource.dateIssued
          );
          
          if (matchingDateItem) {
            setSelectedDate(matchingDateItem);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching model dates:", error);
    } finally {
      setIsLoadingDates(false);
    }
  };

  // Handle model selection
  const handleModelSelect = (modelItem) => {
    setSelectedModel(modelItem);
    
    // Update data source record
    const dataSourceId = Object.keys(dataSourceRecord)[0];
    
    // Log for debugging
    console.log('Selected model full data:', modelItem);
    console.log('Current data source record:', dataSourceRecord[dataSourceId]);
    
    // Check if this model is coming from the GEFSHRRRModelDataSourceForm by checking for specific properties
    if (modelItem.model && modelItem.model.modelType === 'CACHED_FORECAST') {
      console.log('Model from GEFS/HRRR parameter selection');
      // No longer auto-submitting - user clicks the continue button instead
    }
    
    // Make sure we have a proper data property to avoid errors
    if (!dataSourceRecord[dataSourceId].data) {
      dataSourceRecord[dataSourceId].data = {};
    }
    
    // Log the entire model object for detailed inspection
    console.log('=================== SELECTED MODEL DEBUG START ===================');
    console.log('Model item selected for data source:', modelItem);
    console.log('Model properties:', Object.keys(modelItem.model));
    
    // Actually use the label we already calculated and passed in the modelItem This ensures consistency between what's shown in the button and what's stored
    const displayName = modelItem.label;
    
    console.log('Using display name from modelItem.label:', displayName);
    
    // Check for missing critical properties
    if (!modelItem.model.routeShort || !modelItem.model.routeLong) {
      console.warn("Selected model is missing routeShort or routeLong:", {
        id: modelItem.model.id,
        label: modelItem.label,
        hasRouteShort: !!modelItem.model.routeShort,
        hasRouteLong: !!modelItem.model.routeLong
      });
    }
    
    console.log('=================== SELECTED MODEL DEBUG END ===================');
      
    console.log('Using display name:', displayName);
    
    // Reset dates when a new model is selected
    setModelDates([]);
    setSelectedDate(null);
    
    // Fetch available dates for this model
    fetchModelDates(modelItem);
    
    // Create a properly structured data source based on the schema
    const updatedDataSource = {
      ...dataSourceRecord[dataSourceId],
      resource: {
        ...(dataSourceRecord[dataSourceId].resource || {}),
        resourceType: selectedForecastType.value === 'processedModels' ? 'Processed Model' : 'Sensitivity Model',
        fileSelected: modelItem.model.id,
        noLoad: false,
        latest: false,
        recordId: modelItem.model.id,
        // Store route info for reference even if it's missing
        routeShort: modelItem.model.routeShort || null,
        routeLong: modelItem.model.routeLong || null,
        // Flag if this model is missing critical properties
        missingCriticalProperties: !modelItem.model.routeShort || !modelItem.model.routeLong
      },
      // Store the complete model object for reference
      model: modelItem.model,
      attributes: {
        ...(dataSourceRecord[dataSourceId].attributes || {}),
        name: displayName,
        description: `Model from ${selectedAssetObj.name}`,
      },
      config: {
        assetId: selectedAssetObj.id,
        assetName: selectedAssetObj.name,
        modelId: modelItem.model.id,
        modelName: displayName,
        modelType: selectedForecastType.value
      },
      // Initialize data structure to prevent errors
      data: {
        ...(dataSourceRecord[dataSourceId].data || {}),
        status: "unfetched",
        schema: null,
        value: null
      },
      // Add reasonable defaults for renderProps
      renderProps: {
        color: "#" + Math.floor(Math.random()*16777215).toString(16), // Random color
        visible: true,
        variations: {}
      }
    };
    
    onDataSourceChange({
      [dataSourceId]: updatedDataSource
    });
    
    // Move to styling step
    setCurrentStep(SELECTION_STEPS.STYLE);
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    
    // Update the data source record with the selected date
    const dataSourceId = Object.keys(dataSourceRecord)[0];
    
    // Make sure we have the data source
    if (dataSourceRecord[dataSourceId]) {
      const updatedDataSource = {
        ...dataSourceRecord[dataSourceId],
        resource: {
          ...(dataSourceRecord[dataSourceId].resource || {}),
          // Update fileSelected to the actual file path
          fileSelected: date.path,
          latest: false,
          dateIssued: date.dateStr || 'unknown'
        }
      };
      
      onDataSourceChange({
        [dataSourceId]: updatedDataSource
      });
    }
  };
  
  // Back button handlers for each step
  const handleBackFromAsset = () => {
    setSelectedSourceType(null);
    setCurrentStep(SELECTION_STEPS.SOURCE_TYPE);
  };

  const handleBackFromForecastType = () => {
    setSelectedAssetObj(null);
    setCurrentStep(SELECTION_STEPS.ASSET);
  };

  const handleBackFromModel = () => {
    setSelectedForecastType(null);
    setCurrentStep(SELECTION_STEPS.FORECAST_TYPE);
  };

  const handleBackFromStyle = () => {
    if (selectedModel) {
      setSelectedModel(null);
      setCurrentStep(SELECTION_STEPS.MODEL);
    } else if (selectedForecastType) {
      setSelectedForecastType(null);
      setCurrentStep(SELECTION_STEPS.FORECAST_TYPE);
    } else if (selectedAssetObj) {
      setSelectedAssetObj(null);
      setCurrentStep(SELECTION_STEPS.ASSET);
    } else {
      setSelectedSourceType(null);
      setCurrentStep(SELECTION_STEPS.SOURCE_TYPE);
    }
  };

  if (!dataSourceRecord) {
    console.log("Data source record doesn't exist");
    return false;
  }

  // Render the current step
  const renderCurrentStep = () => {
    // Log for debugging
    console.log("Rendering step:", currentStep, "with model:", selectedModel?.id);
    
    // Make sure we have required state for each step
    if ((currentStep === SELECTION_STEPS.STYLE || currentStep === "STYLE") && 
        (!selectedModel || !selectedForecastType)) {
      console.log("Missing required state for STYLE step:", { 
        hasModel: !!selectedModel, 
        hasForecaseType: !!selectedForecastType 
      });
    }
    
    // Normalize currentStep to handle both string and constant
    const normalizedStep = currentStep === "STYLE" ? SELECTION_STEPS.STYLE : currentStep;
    
    switch (normalizedStep) {
      case SELECTION_STEPS.SOURCE_TYPE:
        return (
          <ButtonSelectionList 
            items={DATA_SOURCE_TYPES}
            onSelect={handleSourceTypeSelect}
            categoryName="Data Source Type"
          />
        );
        
      case SELECTION_STEPS.ASSET:
        return (
          <ButtonSelectionList 
            items={formattedAssets}
            onSelect={handleAssetSelect}
            categoryName="Asset"
          />
        );
        
      case SELECTION_STEPS.FORECAST_TYPE:
        return (
          <ButtonSelectionList 
            items={forecastTypes}
            onSelect={handleForecastTypeSelect}
            categoryName="Forecast Type"
          />
        );
        
      case SELECTION_STEPS.MODEL:
        // For GEFS/HRRR forecasts, use our special model selection component
        if (selectedForecastType && 
           (selectedForecastType.value === '240hr3' || selectedForecastType.value === '48hr1')) {
          return (
            <GEFSHRRRModelDataSourceForm 
              forecastType={selectedForecastType}
              selectedAsset={selectedAssetObj}
              onSubmit={handleModelSelect}
            />
          );
        }
        // For other model types, use the standard selection list
        return (
          <ButtonSelectionList 
            items={models}
            onSelect={handleModelSelect}
            categoryName="Model"
          />
        );
        
      case SELECTION_STEPS.STYLE:
        return (
          <div>
            {/* Date selection section */}
            <div className="mb-5 border-b pb-5">
              <h3 className="text-gray-700 font-medium text-sm mb-3">Select a Date</h3>
              
              {isLoadingDates ? (
                <div className="py-4 text-center text-gray-500">
                  <svg className="animate-spin h-5 w-5 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading available versions...
                </div>
              ) : modelDates.length === 0 ? (
                <div className="py-4 text-center border border-gray-200 rounded-md bg-gray-50 text-gray-500">
                  No versions available for this model
                </div>
              ) : (
                <div>
                    <Select
                      value={selectedDate ? selectedDate.eTag : ""}
                      onChange={(e) => {
                        const selectedDateItem = modelDates.find(d => d.eTag === e.target.value);
                        if (selectedDateItem) {
                          handleDateSelect(selectedDateItem);
                        }
                      }}
                      disabled={false}
                    >
                      <option value="" disabled>Select a model version</option>
                      {modelDates.map((date, index) => (
                        <option key={date.eTag || index} value={date.eTag}>
                          {index === 0 ? "Most recent version" : (
                            date.displayDate.startsWith('Date ') 
                              ? `Version ${date.file || date.dateStr}`
                              : date.displayDate
                          )}
                        </option>
                      ))}
                    </Select>
                  
                </div>
              )}
            </div>
            
            {/* Style configuration section */}
            <div className="mb-3">
              <h3 className="text-gray-700 font-medium text-sm">Select Channels</h3>
            </div>
            <DataSourceStyleUpdate dataSourceRecord={dataSourceRecord} onChange={onDataSourceChange} />
          </div>
        );
        
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <div className="flex flex-col w-full">
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-5">
        <div>
          {/* Show selection breadcrumb */}
          <div className="flex mb-5 py-2">
            <div 
              className="cursor-pointer text-gray-700 text-sm font-normal font-Inter mr-1"
              onClick={() => {
                setSelectedSourceType(null);
                setSelectedAssetObj(null);
                setSelectedForecastType(null);
                setSelectedModel(null);
                setCurrentStep(SELECTION_STEPS.SOURCE_TYPE);
              }}
            >
              {currentStep === SELECTION_STEPS.SOURCE_TYPE ? <span className="text-blue-600 font-medium">Data Source</span> : "Data Source"}
            </div>
            {selectedSourceType && (
              <>
                <svg className="mr-1" stroke="#A2BBBF" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 15L12.5 10L7.5 5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div 
                  className="cursor-pointer text-gray-700 text-sm font-normal font-Inter mr-1"
                  onClick={() => {
                    setSelectedAssetObj(null);
                    setSelectedForecastType(null);
                    setSelectedModel(null);
                    setCurrentStep(SELECTION_STEPS.ASSET);
                  }}
                >
                  {currentStep === SELECTION_STEPS.ASSET ? <span className="text-blue-600 font-medium">Asset</span> : "Asset"}
                </div>
              </>
            )}
            {selectedAssetObj && (
              <>
                <svg className="mr-1" stroke="#A2BBBF" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 15L12.5 10L7.5 5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div 
                  className="cursor-pointer text-gray-700 text-sm font-normal font-Inter mr-1"
                  onClick={() => {
                    setSelectedForecastType(null);
                    setSelectedModel(null);
                    setCurrentStep(SELECTION_STEPS.FORECAST_TYPE);
                  }}
                >
                  {currentStep === SELECTION_STEPS.FORECAST_TYPE ? <span className="text-blue-600 font-medium">Forecast Type</span> : "Forecast Type"}
                </div>
              </>
            )}
            {selectedForecastType && (selectedForecastType.value.includes('Models') || 
             selectedForecastType.value === '240hr3' || selectedForecastType.value === '48hr1') && (
              <>
                <svg className="mr-1" stroke="#A2BBBF" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 15L12.5 10L7.5 5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div 
                  className="cursor-pointer text-gray-700 text-sm font-normal font-Inter mr-1"
                  onClick={() => {
                    setSelectedModel(null);
                    setCurrentStep(SELECTION_STEPS.MODEL);
                  }}
                >
                  {currentStep === SELECTION_STEPS.MODEL ? 
                    <span className="text-blue-600 font-medium">
                      {(selectedForecastType.value === '240hr3' || selectedForecastType.value === '48hr1') 
                        ? 'Parameter' 
                        : 'Model'}
                    </span> 
                    : 
                    (selectedForecastType.value === '240hr3' || selectedForecastType.value === '48hr1') 
                      ? 'Parameter' 
                      : 'Model'
                  }
                </div>
              </>
            )}
            {/* Show Style in the breadcrumb if we have a selected model OR if we're in the style step */}
            {(selectedModel || 
              currentStep === SELECTION_STEPS.STYLE || 
              (selectedForecastType && 
                !selectedForecastType.value.includes('Models') && 
                selectedForecastType.value !== '240hr3' && 
                selectedForecastType.value !== '48hr1')) && (
              <>
                <svg className="mr-1" stroke="#A2BBBF" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 15L12.5 10L7.5 5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="text-gray-700 text-sm font-normal font-Inter mr-1">
                  {currentStep === SELECTION_STEPS.STYLE ? <span className="text-blue-600 font-medium">Style</span> : "Style"}
                </div>
              </>
            )}
          </div>
          
          {/* Current step content */}
          {renderCurrentStep()}
        </div>
      </div>
      
      {/* Action Buttons - only show when on the style step and not in auto-submit mode */}
      {(currentStep === SELECTION_STEPS.STYLE || currentStep === "STYLE") && (
        <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between px-4">
          <div className="text-gray-500 text-sm self-center">
            {/* Show appropriate guidance based on state */}
            {autoSubmitMode ? "Adjusting styling options..." :
             (modelDates.length > 0 && !selectedDate && initialStep !== SELECTION_STEPS.STYLE && 
              "Please select a date to continue")}
          </div>
          
          {/* Only show buttons if not in auto-submit mode */}
          {!autoSubmitMode && (
            <div>
              <button 
                onClick={() => onCancel ? onCancel() : onSubmitForm()} 
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 mr-3"
              >
                Cancel
              </button>
              <button 
                onClick={() => onSubmitForm()} 
                disabled={modelDates.length > 0 && !selectedDate && initialStep !== SELECTION_STEPS.STYLE}
                className={`px-4 py-2 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  modelDates.length > 0 && !selectedDate && initialStep !== SELECTION_STEPS.STYLE
                    ? "bg-blue-300 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                }`}
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ButtonSelectionDataSourceForm;